import apiClient from './client';
import { Incident, CreateIncidentRequest, IncidentResponse, IncidentStats, IncidentAnalysis, PaginatedIncidents } from '../types/incident';
import { API_ENDPOINTS } from './endpoints';
import { getUsers } from './user';

class IncidentAPI {
    private static normalizeIncident(incident: any, usersMap?: Map<string, { id: string; name: string; email: string }>): Incident {
        // Normalize history field - ensure it's an array
        let history = incident.history;
        if (history && !Array.isArray(history)) {
            history = [history];
        }
        
        // Enrich incident with user data if available
        const enrichedIncident = {
            ...incident,
            history: history || []
        };

        if (usersMap && incident.user_id && usersMap.has(incident.user_id)) {
            enrichedIncident.user = usersMap.get(incident.user_id);
        }

        return enrichedIncident;
    }

    private static normalizeSuggestion(suggestion: any): IncidentAnalysis {
        let confidence = 0;
        if (typeof suggestion.confidence === 'number') {
            confidence = suggestion.confidence;
        } else if (typeof suggestion.confidence === 'string') {
            const confStr = suggestion.confidence.replace(/[^0-9.]/g, '');
            confidence = parseFloat(confStr);
            if (confidence > 1) {
                confidence = confidence / 100;
            }
        }

        return {
            ...suggestion,
            potential_solution: suggestion.suggested_solution || suggestion.potential_solution,
            assigned_to: suggestion.assign_to || suggestion.assigned_to,
            confidence_score: confidence,
            summary: suggestion.comments || suggestion.summary || suggestion.title,
            potential_cause: suggestion.potential_cause,
            estimated_resolution_time: suggestion.estimated_resolution_time,
            next_steps: Array.isArray(suggestion.next_steps) ? suggestion.next_steps : []
        };
    }

    static async createIncident(data: CreateIncidentRequest): Promise<IncidentResponse> {
        const response = await apiClient.post(API_ENDPOINTS.INCIDENTS, data);
        return response.data;
    }

    static async getIncidents(page: number = 1, limit: number = 10, sortBy: string = 'created_at', order: string = 'desc', companyId?: string): Promise<PaginatedIncidents> {
        const params: any = { page, limit, sortBy, order };
        if (companyId) {
            params.company_id = companyId;
        }
        
        // Fetch users to enrich incident data
        let usersMap: Map<string, { id: string; name: string; email: string }> = new Map();
        try {
            const users = await getUsers();
            usersMap = new Map(users.map(u => [u.id, { id: u.id, name: u.name, email: u.email }]));
        } catch (error) {
            console.warn('Failed to fetch users for enrichment:', error);
        }

        const response = await apiClient.get(API_ENDPOINTS.INCIDENTS, { params });
        return {
            ...response.data,
            incidents: response.data.incidents.map((incident: any) => this.normalizeIncident(incident, usersMap))
        };
    }

    static async getIncident(id: string): Promise<Incident> {
        // Fetch users to enrich incident data
        let usersMap: Map<string, { id: string; name: string; email: string }> = new Map();
        try {
            const users = await getUsers();
            usersMap = new Map(users.map(u => [u.id, { id: u.id, name: u.name, email: u.email }]));
        } catch (error) {
            console.warn('Failed to fetch users for enrichment:', error);
        }

        const response = await apiClient.get(`${API_ENDPOINTS.INCIDENTS}/${id}`);
        return this.normalizeIncident(response.data, usersMap);
    }

    static async updateIncident(id: string, data: Partial<Incident>): Promise<IncidentResponse> {
        const response = await apiClient.patch(`${API_ENDPOINTS.INCIDENTS}/${id}`, data);
        return response.data;
    }

    static async deleteIncident(id: string): Promise<void> {
        await apiClient.delete(`${API_ENDPOINTS.INCIDENTS}/${id}`);
    }

    static async getIncidentStats(): Promise<IncidentStats> {
        const response = await apiClient.get(API_ENDPOINTS.INCIDENT_STATS);
        const data = response.data;
        return {
            total_incidents: data.total_incidents,
            resolved_incidents: data.resolved_incidents,
            pending_incidents: (data.open_incidents || 0) + (data.in_progress_incidents || 0),
            average_resolution_time: data.average_resolution_time || 'N/A',
            most_common_category: 'N/A'
        };
    }

    static async getIncidentSuggestions(id: string): Promise<IncidentAnalysis[]> {
        const response = await apiClient.get(API_ENDPOINTS.SUGGESTIONS(id));
        const data = response.data;

        if (data.suggestions && Array.isArray(data.suggestions)) {
            return data.suggestions.map((s: any) => IncidentAPI.normalizeSuggestion(s));
        }

        return [IncidentAPI.normalizeSuggestion(data)];
    }
}

export default IncidentAPI;
